import React, { createContext, useContext, useState, useCallback } from "react";
import {
  parseUISettings,
  formatUISettings,
} from "../utils/userPreferencesHelper";
import userPreferenceApiService from "../services/user-preference-api";

interface LinkOrderContextType {
  linkOrder: string[];
  setLinkOrder: (order: string[]) => void;
  reorderLinks: <T extends { id?: string }>(links: T[]) => T[];
  initializeFromPreferences: (settingsField: string | undefined | null) => void;
}

const LinkOrderContext = createContext<LinkOrderContextType | undefined>(
  undefined,
);

const LINK_ORDER_KEY = "link-order";

export const LinkOrderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [linkOrder, setLinkOrderState] = useState<string[]>(() => {
    const saved = localStorage.getItem(LINK_ORDER_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return [];
      }
    }
    return [];
  });

  const setLinkOrder = useCallback(async (order: string[]) => {
    setLinkOrderState(order);
    localStorage.setItem(LINK_ORDER_KEY, JSON.stringify(order));

    // Save to backend
    try {
      const currentPrefs = await userPreferenceApiService.getMyPreferences();
      const uiSettings = parseUISettings(currentPrefs.settings);
      uiSettings.linkOrder = order;
      const updatedSettingsField = formatUISettings(uiSettings);
      await userPreferenceApiService.saveMyPreferences({
        ...currentPrefs,
        settings: updatedSettingsField,
      });
    } catch (error) {
      console.error("Failed to save link order to backend:", error);
    }
  }, []);

  const reorderLinks = useCallback(
    <T extends { id?: string }>(links: T[]): T[] => {
      if (!linkOrder || linkOrder.length === 0) {
        return links;
      }

      const linkMap = new Map(
        links.map((link) => [link.id?.toString() || "", link]),
      );
      const ordered: T[] = [];

      // Add links in saved order
      for (const id of linkOrder) {
        const link = linkMap.get(id);
        if (link) {
          ordered.push(link);
          linkMap.delete(id);
        }
      }

      // Append any new links not in the saved order
      for (const link of linkMap.values()) {
        ordered.push(link);
      }

      return ordered;
    },
    [linkOrder],
  );

  const initializeFromPreferences = useCallback(
    (settingsField: string | undefined | null) => {
      const uiSettings = parseUISettings(settingsField);
      if (uiSettings.linkOrder && uiSettings.linkOrder.length > 0) {
        setLinkOrderState(uiSettings.linkOrder);
        localStorage.setItem(
          LINK_ORDER_KEY,
          JSON.stringify(uiSettings.linkOrder),
        );
      }
    },
    [],
  );

  return (
    <LinkOrderContext.Provider
      value={{ linkOrder, setLinkOrder, reorderLinks, initializeFromPreferences }}
    >
      {children}
    </LinkOrderContext.Provider>
  );
};

export const useLinkOrder = (): LinkOrderContextType => {
  const context = useContext(LinkOrderContext);
  if (!context) {
    throw new Error("useLinkOrder must be used within a LinkOrderProvider");
  }
  return context;
};
